<template>
  <aside class="education-sidebar" :class="{ 'is-collapsed': collapsed }">
    <button class="education-brand" type="button" title="返回应用中心" @click="backToCenter">
      <span class="brand-mark"><Icon icon="ep:reading" :size="20" /></span>
      <span v-if="!collapsed" class="brand-name">AI 教育</span>
    </button>

    <nav class="education-navigation" aria-label="AI 教育菜单">
      <template v-for="(item, index) in items" :key="item.section ? `sec-${index}` : item.path">
        <div v-if="item.section" class="education-menu-section">
          <span v-if="!collapsed">{{ item.title }}</span>
        </div>
        <RouterLink
          v-else
          :to="item.query ? { path: item.path, query: item.query } : item.path"
          class="education-menu-item"
          :class="{ active: isActive(item) }"
          :title="collapsed ? item.title : undefined"
        >
          <Icon :icon="item.icon || 'ep:menu'" :size="18" />
          <span v-if="!collapsed">{{ item.title }}</span>
        </RouterLink>
      </template>
      <div v-if="!items.length && !collapsed" class="education-menu-empty">菜单加载中</div>
    </nav>

    <button
      class="sidebar-toggle"
      type="button"
      :aria-label="collapsed ? '展开菜单' : '收起菜单'"
      @click="$emit('toggle')"
    >
      <Icon :icon="collapsed ? 'ep:expand' : 'ep:fold'" :size="18" />
    </button>
  </aside>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import type { EducationMenuItem } from './types'

defineOptions({ name: 'AiEducationSidebar' })

defineProps<{ collapsed: boolean; items: EducationMenuItem[] }>()
defineEmits<{ toggle: [] }>()

const route = useRoute()

const isActive = (item: EducationMenuItem) =>
  route.path === item.path || item.activePaths?.some((path) => route.path.startsWith(path))

const backToCenter = () => {
  window.location.href = new URL('/ai-Application-center', window.location.origin).href
}
</script>

<style scoped>
/* 对齐 StandaloneAppShell 其他 AI 应用侧栏：深蓝底 #1e3a5f */
.education-sidebar {
  display: flex;
  min-width: 0;
  height: 100vh;
  flex-direction: column;
  overflow: hidden;
  color: #bccde0;
  background: #1e3a5f;
  transition: width 0.2s ease;
}

.education-brand {
  display: flex;
  height: 56px;
  flex: 0 0 56px;
  align-items: center;
  gap: 10px;
  padding: 0 18px;
  margin: 0;
  cursor: pointer;
  border: 0;
  border-bottom: 1px solid rgb(255 255 255 / 8%);
  color: inherit;
  background: transparent;
  text-align: left;
}

.education-brand:hover {
  background: rgb(255 255 255 / 4%);
}

.brand-mark {
  display: grid;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  place-items: center;
  border-radius: 6px;
  color: #1e3a5f;
  background: #3b9eff;
}

.brand-name {
  overflow: hidden;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.education-navigation {
  flex: 1;
  overflow: auto;
  padding: 8px 0 12px;
}

.education-menu-section {
  display: flex;
  min-height: 28px;
  align-items: center;
  margin: 10px 18px 4px;
  color: rgb(188 205 224 / 55%);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: none;
}

.education-menu-item {
  position: relative;
  display: flex;
  min-height: 44px;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding: 0 18px;
  color: #bccde0;
  text-decoration: none;
  transition:
    color 0.15s ease,
    background-color 0.15s ease;
}

.education-menu-item:hover {
  color: #fff;
  background: rgb(255 255 255 / 6%);
}

.education-menu-item.active {
  color: #fff;
  font-weight: 600;
  background: rgb(36 75 130 / 50%);
}

.education-menu-item.active::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 3px;
  content: '';
  background: #3b9eff;
}

.education-menu-empty {
  padding: 18px 18px;
  color: rgb(188 205 224 / 60%);
  font-size: 13px;
}

.sidebar-toggle {
  display: grid;
  width: 100%;
  height: 48px;
  flex: 0 0 48px;
  cursor: pointer;
  place-items: center;
  border: 0;
  border-top: 1px solid rgb(255 255 255 / 8%);
  color: #bccde0;
  background: transparent;
}

.sidebar-toggle:hover {
  color: #fff;
  background: rgb(255 255 255 / 6%);
}

.is-collapsed .education-brand {
  justify-content: center;
  padding: 0;
}

.is-collapsed .education-navigation {
  padding-inline: 0;
}

.is-collapsed .education-menu-item {
  justify-content: center;
  padding: 0;
}

.is-collapsed .education-menu-section {
  margin-inline: 8px;
  justify-content: center;
}
</style>
